export type SearchableEntityType =
  | 'customers'
  | 'companies'
  | 'pnt'
  | 'invoices'
  | 'suppliers'
  | 'travelers'
  | 'pnrs'
  | 'tickets'
  | 'trips'
  | 'documents'
  | 'tasks';

export const ENTITY_PERMISSION_PATHS: Record<SearchableEntityType, readonly string[]> = {
  customers: ['/crm/customers'],
  companies: ['/crm/companies'],
  pnt: ['/pnt'],
  invoices: ['/invoices'],
  suppliers: ['/crm/suppliers'],
  travelers: ['/crm/travelers'],
  pnrs: ['/pnr'],
  tickets: ['/ucc'],
  trips: ['/trips'],
  documents: ['/crm/documents'],
  tasks: ['/tasks/management'],
};

/**
 * Search actions may target child routes that are not represented as sidebar
 * entries. DashboardLayout evaluates these paths through the same route
 * permission service before the palette can expose or execute those actions.
 */
export const ADDITIONAL_SEARCH_PERMISSION_PATHS = [
  '/pnr/new',
  '/rtw/assistant',
  '/invoices/new',
  '/crm/customers',
  '/crm/companies',
  '/crm/travelers',
  '/crm/documents',
] as const;

export const NAVIGATION_SEARCH_ALIASES: Record<string, readonly string[]> = {
  dashboard: ['home', 'overview', 'main dashboard', 'kpi', 'command center'],
  'ops-today': ['today board', 'daily operations', 'departures today', 'morning triage'],
  'ops-inbox': ['work queue', 'cockpit', 'decisions', 'operations inbox'],
  'ops-supervisor': ['dispatch', 'assign work', 'team workload', 'supervisor dispatch'],
  'ops-control-tower': ['source health', 'system status', 'operational trust', 'control tower'],
  'ops-handbook': ['handbook', 'sop', 'standard operating procedures', 'runbook', 'operations manual'],
  aoc: ['aviation operations', 'flight disruption', 'traveller monitoring', 'airlines operations center'],
  'unified-comms': ['ucc', 'freshdesk', 'whatsapp', 'email tickets', 'unified communications', 'inbox'],
  tasks: ['task dashboard', 'to do', 'todo', 'action items', 'staff tasks'],
  'task-management': ['tasks', 'to do', 'todo', 'action items', 'staff tasks'],
  'ticketing-deadlines': ['ticketing deadlines', 'ttl', 'time limits', 'expiry', 'airline deadlines'],
  pnr: ['booking file', 'record locator', 'passenger name record', 'gds booking'],
  'interface-import': ['import pnr', 'paste pnr', 'sabre import', 'amadeus import'],
  pnt: ['paid not ticketed', 'pending tickets', 'ticketing urgency', 'payment ticketing'],
  'enhanced-bookings': ['enhanced bookings', 'reservations', 'booking management', 'active bookings'],
  trips: ['trip management', 'journeys', 'client trips', 'hx', 'cancelled segments'],
  'air-itinerary-composer': ['all air', 'client itinerary', 'email itinerary', 'paste flights', 'copy flights'],
  'itinerary-builder': ['trip builder', 'proposal builder', 'day by day itinerary'],
  'advanced-itinerary': ['client proposals', 'travel proposals', 'trip proposals', 'quote management', 'price quotes'],
  'proposal-tracking': ['proposal tracking', 'quote status', 'sent proposals'],
  rtw: ['round the world', 'oneworld planner', 'tier points', 'rtw builder', 'validate route'],
  'rtw-shared-links': ['client proposal', 'published proposal', 'share itinerary', 'shared links'],
  'tier-points-calculator': ['british airways', 'ba tier points', 'executive club', 'oneworld status', 'avios'],
  'queue-center': ['queue monitor', 'sabre queue', 'amadeus queue', 'gds queue'],
  'queue-actions': ['queue automation', 'queue rules', 'gds queue rules'],
  'sabre-dashboard': ['gds', 'sabre tools', 'sabre workspace'],
  'sabre-cryptic-command': ['cryptic terminal', 'gds terminal', 'sabre command'],
  'amadeus-terminal': ['cryptic terminal', 'gds terminal', 'amadeus command'],
  'customer-directory': ['clients', 'customer directory', 'passenger profiles', 'crm customers'],
  customers: ['crm home', 'clients', 'customer workspace'],
  companies: ['corporate accounts', 'business clients', 'b2b accounts', 'organisations'],
  travelers: ['passengers', 'travellers', 'pax profiles', 'passports', 'frequent flyers'],
  leads: ['sales leads', 'enquiries', 'prospects', 'pipeline'],
  'customer-segments': ['customer segments', 'vip tiers', 'marketing lists', 'cohorts', 'segmentation'],
  'crm-suppliers': ['vendors', 'airlines', 'dmc', 'tour operators', 'supplier directory', 'supplier 360'],
  'crm-hotels': ['hotel properties', 'accommodation', 'preferred hotels', 'resort directory'],
  'partners-all': ['referral partners', 'affiliates', 'external advisors', 'partner network'],
  documents: ['document vault', 'passport scans', 'visas', 'contracts', 'client files'],
  'crm-contact-matching': ['duplicate contacts', 'identity resolution', 'merge contacts', 'unmatched emails'],
  finance: ['finance overview', 'cashflow', 'financial summary', 'treasury', 'finance dashboard'],
  invoices: ['client invoices', 'billing', 'accounts receivable', 'sales invoices'],
  payments: ['stripe payments', 'card payments', 'transactions', 'payment links', 'refunds'],
  banking: ['plaid', 'bank accounts', 'bank feeds', 'reconciliation', 'mercury', 'wise'],
  'ar-aging': ['accounts receivable aging', 'overdue invoices', 'debtors', 'unpaid balances'],
  commissions: ['agent commissions', 'supplier commissions', 'payouts', 'margin split'],
  'supplier-invoices': ['invoice pdf', 'gmail invoices', 'supplier documents', 'accounts payable', 'ap'],
  'supplier-pollers': ['gmail ingestion', 'document ingestion', 'invoice parsing', 'ap pollers'],
  'fora-integration': ['fora commission', 'advisor portal', 'fora bookings'],
  'fora-sync': ['fora automation', 'fora stale', 'advisor portal sync'],
  'portal-profiles': ['browser profile', 'fora login', 'supplier portal'],
  reports: ['business intelligence', 'bi reports', 'financial reports', 'kpi reports'],
  'agent-performance': ['staff performance', 'advisor leaderboard', 'conversion rates', 'sales metrics'],
  'ai-assistant': ['copilot', 'travel ai', 'chat assistant', 'llm helper'],
  'ai-usage-stats': ['openrouter', 'model usage', 'ai cost', 'llm spend', 'token usage'],
  'admin-dashboard': ['system administration', 'admin home', 'platform control'],
  'user-management': ['user management', 'staff accounts', 'logins', 'team members', 'bots'],
  permissions: ['roles', 'access control', 'rbac', 'route permissions', 'security matrix'],
  automations: ['automation control center', 'cron jobs', 'scheduled tasks', 'background workers', 'workflows'],
  integrations: ['api connections', 'connected systems', 'credentials', 'webhooks', 'third party apis'],
  'system-health': ['status header', 'openrouter health', 'fora health', 'service health', 'diagnostics'],
  'system-manual': ['architecture flowcharts', 'what talks to what', 'technical documentation', 'platform manual'],
  'queue-manual': ['staff manuals', 'user guides', 'training docs', 'how to guides'],
  'fd-import': ['freshdesk sync', 'freshdesk ingestion', 'ticket import'],
  'booking-queue': ['pnr ingestion', 'booking ingestion', 'automation queue'],
  settings: ['preferences', 'configuration', 'profile settings', 'timezone'],
};

export interface NavigationSearchCandidate<T> {
  item: T;
  label: string;
  keywords?: readonly string[];
}

export type NavigationDomainId = 'operations' | 'crm' | 'finance' | 'intelligence' | 'admin';

export interface NavigationPageSourceItem<TIcon> {
  id: string;
  name: string;
  icon: TIcon;
  path: string;
}

export interface NavigationPageSourceSection<TIcon> {
  id: string;
  name: string;
  description?: string;
  tab: NavigationDomainId;
  items: NavigationPageSourceItem<TIcon>[];
}

export interface NavigationPageSearchResult<TIcon> {
  id: string;
  type: 'feature';
  title: string;
  subtitle: string;
  icon: TIcon;
  url: string;
  domain: string;
  keywords: readonly string[];
}

export function getNavigationPermissionPath(path: string): string {
  const [pathname] = path.split(/[?#]/, 1);
  const normalizedPath = pathname.trim();
  if (!normalizedPath || normalizedPath === '/') return '/';
  return normalizedPath.startsWith('/') ? normalizedPath : `/${normalizedPath}`;
}

export function areNavigationPathsAllowed(
  allowedPaths: ReadonlySet<string>,
  requiredPaths: readonly string[],
): boolean {
  return requiredPaths.every((path) => allowedPaths.has(getNavigationPermissionPath(path)));
}

export function getAllowedShortcutTarget(
  allowedPaths: ReadonlySet<string>,
  preferredPath: string,
  fallbackPaths: readonly string[],
): string | null {
  if (allowedPaths.has(getNavigationPermissionPath(preferredPath))) return preferredPath;
  return fallbackPaths.find((path) => allowedPaths.has(getNavigationPermissionPath(path))) ?? null;
}

const DYNAMIC_ROUTE_PERMISSION_PREFIXES = [
  '/crm/customers',
  '/crm/companies',
  '/crm/travelers',
  '/crm/suppliers',
  '/crm/documents',
  '/pnr',
  '/invoices',
  '/trips',
  '/ucc',
  '/tasks',
] as const;

export function getNavigationAuthorizationPath(path: string): string {
  const normalized = getNavigationPermissionPath(path);
  if (ADDITIONAL_SEARCH_PERMISSION_PATHS.includes(normalized as typeof ADDITIONAL_SEARCH_PERMISSION_PATHS[number])) {
    return normalized;
  }
  for (const prefix of DYNAMIC_ROUTE_PERMISSION_PREFIXES) {
    if (normalized.startsWith(`${prefix}/`)) return prefix;
  }
  return normalized;
}

const DOMAIN_LABELS: Record<NavigationDomainId, string> = {
  operations: 'Operations',
  crm: 'CRM',
  finance: 'Finance',
  intelligence: 'Intelligence',
  admin: 'Admin',
};

export const NAVIGATION_PAGE_SUBTITLES: Record<string, string> = {
  dashboard: 'Executive & daily operational KPI overview, urgent alerts, and quick actions',
  'ops-today': 'Departures, arrivals, ticketing deadlines, and urgent items due today',
  'ops-inbox': 'Prioritised operational work queue and customer case triage',
  'ops-supervisor': 'Team workload balancing, case assignment, and supervisor dispatch',
  'ops-control-tower': 'Source freshness, ingestion health, and operational trust monitors',
  'ops-handbook': 'Standard operating procedures, ticketing runbooks, and escalation guides',
  aoc: 'Live flight disruption monitoring, schedule changes, and traveller risk alerts',
  'unified-comms': 'Unified customer inbox across Freshdesk email tickets and WhatsApp threads',
  tasks: 'Staff action items, follow-ups, and customer/booking-linked tasks',
  'task-management': 'Create, assign, filter, and track staff tasks linked to customers and PNRs',
  'ticketing-deadlines': 'Upcoming airline ticketing time limits (TTL) and fare expiry countdowns',
  pnr: 'Retrieve, inspect, and sync Sabre & Amadeus Passenger Name Records',
  'interface-import': 'Import Sabre IUR and Amadeus AIR back-office interface files',
  pnt: 'Paid-not-ticketed ledger, coupon status verification, and ticketing urgency',
  'enhanced-bookings': 'All active, hold/quote, and ticketed reservations with lifecycle status',
  trips: 'Client journeys, multi-PNR trip folders, and HX cancelled-segment alerts',
  'air-itinerary-composer': 'Compose clean branded air itineraries from PNR segments for clients',
  'itinerary-builder': 'Build day-by-day multi-component travel itineraries and proposals',
  'advanced-itinerary': 'Manage client quotes, travel proposals, and pricing revisions',
  'proposal-tracking': 'Track published proposals, client views, and acceptance status',
  rtw: 'Build, validate (Rule 3015), price, and optimise oneworld Round-the-World routes',
  'rtw-shared-links': 'Manage published RTW client proposal links and viewing permissions',
  'tier-points-calculator': 'Calculate BA Club, Finnair Plus, Qpoints, and Atmos status earnings',
  'queue-center': 'Monitor and action live Sabre and Amadeus GDS queues',
  'queue-actions': 'Configure automated GDS queue sweep rules and routing actions',
  'sabre-dashboard': 'Sabre GDS tools, office emulation, and entitlement checks',
  'sabre-cryptic-command': 'Execute raw Sabre cryptic host commands in an audited terminal',
  'amadeus-terminal': 'Execute Amadeus cryptic commands and inspect PNR/ticket state',
  'customer-directory': 'Search, filter, and manage all CRM customer profiles and cohorts',
  customers: 'Customer-first CRM workspace, cohorts, and duplicate review',
  companies: 'Corporate B2B accounts, linked travellers, and company billing',
  travelers: 'Individual passenger profiles, passports, visas, and frequent flyer numbers',
  leads: 'Inbound sales enquiries, qualification scoring, and conversion pipeline',
  'customer-segments': 'Dynamic customer cohorts, VIP tiers, and segmentation rules',
  'crm-suppliers': 'Supplier 360 directory, settlement terms, invoices, and linked hotels',
  'crm-hotels': 'Preferred hotel properties, luxury consortia perks, and DMC contacts',
  'partners-all': 'External referral partners, sub-agents, and commission splits',
  documents: 'Secure travel document vault for passport scans, visas, and contracts',
  'crm-contact-matching': 'Link unmatched conversations to CRM customers and review form details',
  finance: 'Revenue, receivables, supplier payables, and cashflow summary',
  invoices: 'Create, issue, and track client sales invoices and balances',
  payments: 'Stripe card payments, payment links, allocations, and refund audit',
  banking: 'Plaid bank feeds, transaction categorisation, and spend analytics',
  'ar-aging': 'Overdue client receivables broken down by 30/60/90+ day aging buckets',
  commissions: 'Track expected vs. received supplier and partner commissions by currency',
  'supplier-invoices': 'Upload and reconcile AerTicket and supplier invoice PDFs against bookings',
  'supplier-pollers': 'Monitor automated Gmail supplier invoice and statement ingestion jobs',
  'fora-integration': 'Reconcile Fora advisor portal bookings and hotel commission payouts',
  'fora-sync': 'Monitor Fora portal sync freshness and resolve unmatched stays',
  'portal-profiles': 'Manage authenticated browser profiles for supplier portal automation',
  reports: 'Financial, operational, ingestion, and executive business intelligence reports',
  'agent-performance': 'Advisor sales volume, response times, and conversion scorecards',
  'ai-assistant': 'AI copilot for fare rule lookups, PNR interpretation, and drafting',
  'ai-usage-stats': 'OpenRouter model spend, token volume, and latency telemetry',
  'admin-dashboard': 'Platform administration, security controls, and governance overview',
  'user-management': 'Manage staff accounts, roles, approvals, and Bot/QA filters',
  permissions: 'Role-based access control (RBAC) matrix and route permission guards',
  automations: 'Automation Control Center for background jobs, switches, and cron schedules',
  integrations: 'Unified 5-store API credential health, connected services, and webhooks',
  'system-health': 'Live diagnostics for Supabase, OpenRouter, GDS, Freshdesk, and pollers',
  'system-manual': 'Technical architecture flowcharts, data flows, and system reference',
  'queue-manual': 'Step-by-step staff training manuals and operational playbooks',
  'fd-import': 'Synchronise and audit mirrored Freshdesk tickets and conversations',
  'booking-queue': 'Monitor automated PNR and booking ingestion queue runs',
  settings: 'Personal profile, appearance theme, presentation PIN, and client switches',
};

export function buildNavigationPageSearchResults<TIcon>(
  sections: readonly NavigationPageSourceSection<TIcon>[],
): NavigationPageSearchResult<TIcon>[] {
  return sections.flatMap((section) => section.items.map((item) => {
    const itemSubtitle = NAVIGATION_PAGE_SUBTITLES[item.id];
    const subtitle = itemSubtitle
      ? `${section.name} · ${itemSubtitle}`
      : section.description
        ? `${section.name} · ${section.description}`
        : section.name;
    return {
      id: `page-${item.id}`,
      type: 'feature' as const,
      title: item.name,
      subtitle,
      icon: item.icon,
      url: item.path,
      domain: DOMAIN_LABELS[section.tab],
      keywords: [
        item.id,
        item.path,
        section.name,
        section.description ?? '',
        itemSubtitle ?? '',
        ...(NAVIGATION_SEARCH_ALIASES[item.id] ?? []),
      ],
    };
  }));
}

function normalize(value: string): string {
  return value
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function acronym(value: string): string {
  return normalize(value)
    .split(' ')
    .filter(Boolean)
    .map((word) => word[0])
    .join('');
}

function isSubsequence(query: string, candidate: string): boolean {
  let queryIndex = 0;
  for (let index = 0; index < candidate.length && queryIndex < query.length; index += 1) {
    if (candidate[index] === query[queryIndex]) queryIndex += 1;
  }
  return queryIndex === query.length;
}

function editDistanceAtMostOne(left: string, right: string): boolean {
  if (Math.abs(left.length - right.length) > 1) return false;
  if (left === right) return true;

  let leftIndex = 0;
  let rightIndex = 0;
  let edits = 0;
  while (leftIndex < left.length && rightIndex < right.length) {
    if (left[leftIndex] === right[rightIndex]) {
      leftIndex += 1;
      rightIndex += 1;
      continue;
    }

    edits += 1;
    if (edits > 1) return false;
    if (left.length > right.length) leftIndex += 1;
    else if (right.length > left.length) rightIndex += 1;
    else {
      leftIndex += 1;
      rightIndex += 1;
    }
  }

  if (leftIndex < left.length || rightIndex < right.length) edits += 1;
  return edits <= 1;
}

export function fuzzyNavigationScore(query: string, terms: readonly string[]): number {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) return 0;

  const queryTokens = normalizedQuery.split(' ').filter(Boolean);
  let bestScore = -1;
  const corpusWords: string[] = [];

  for (const rawTerm of terms) {
    const term = normalize(rawTerm);
    if (!term) continue;
    const words = term.split(' ').filter(Boolean);
    corpusWords.push(...words);

    if (term === normalizedQuery) bestScore = Math.max(bestScore, 120);
    else if (term.startsWith(normalizedQuery)) bestScore = Math.max(bestScore, 105);
    else if (term.includes(normalizedQuery)) bestScore = Math.max(bestScore, 90);

    if (acronym(term) === normalizedQuery.replace(/\s/g, '')) {
      bestScore = Math.max(bestScore, 110);
    }

  }

  // A query often spans the visible label and one or more aliases (for example
  // "openrouter status" -> "System Health" + "openrouter health"). Match each
  // token against the combined candidate vocabulary instead of requiring every
  // token to occur in one alias string.
  const allTokensMatch = queryTokens.every((token) => corpusWords.some((word) =>
    word.startsWith(token)
    || word.includes(token)
    || (token.length >= 3 && isSubsequence(token, word))
    || (token.length >= 4 && editDistanceAtMostOne(token, word)),
  ));

  if (allTokensMatch) {
    const prefixMatches = queryTokens.filter((token) => (
      corpusWords.some((word) => word.startsWith(token))
    )).length;
    bestScore = Math.max(bestScore, 65 + prefixMatches * 5);
  }

  return bestScore;
}

export function rankNavigationCandidates<T>(
  query: string,
  candidates: readonly NavigationSearchCandidate<T>[],
): T[] {
  return candidates
    .map((candidate, index) => ({
      item: candidate.item,
      index,
      score: fuzzyNavigationScore(query, [candidate.label, ...(candidate.keywords ?? [])]),
    }))
    .filter((candidate) => candidate.score >= 0)
    .sort((left, right) => right.score - left.score || left.index - right.index)
    .map((candidate) => candidate.item);
}

export function getAllowedEntitySearchTypes(allowedPaths: ReadonlySet<string>): Set<SearchableEntityType> {
  const allowedTypes = new Set<SearchableEntityType>();
  for (const [type, permissionPaths] of Object.entries(ENTITY_PERMISSION_PATHS) as Array<
    [SearchableEntityType, readonly string[]]
  >) {
    if (permissionPaths.some((path) => allowedPaths.has(path))) allowedTypes.add(type);
  }
  return allowedTypes;
}
